import React, { useState, useEffect, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineTagGroup } from 'api/types';

/**
 * The tag group dialog: what a group asks about a dish, and the answers it
 * allows.
 *
 * A group is a QUESTION ("how spicy?", "contains?"), its tags are the ANSWERS
 * a dish can give. Three settings change how the question behaves:
 *
 *   select mode   ONE  - a dish gives one answer (spice level)
 *                 MANY - several can be true at once (allergens)
 *   inherited     a menu group may answer for all its dishes; a dish overrides
 *                 only when it differs, or says "none" on purpose
 *   danger        safety information (allergens): shown in red, never hidden
 *
 * A BOUND group (one that writes a dish column, like Food type -> is_veg) is
 * seeded, not created here: binding is a code concern, and the API does not
 * accept it. Its tags carry a code - the value written to the column - which
 * this dialog shows and lets you edit only for such a group.
 */

interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: DineTagGroup | null;
  onSave: () => void;
  onClose: () => void;
}

interface TagRow {
  id?: string;
  tagName: string;
  tagCode: string;
  displayOrder: number;
}

/** CODE_LIKE_THIS, built from the name the user types. Editable before save. */
const codeFrom = (name: string) =>
  name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30);

const DineOpsTagGroupModal: React.FC<Props> = ({ isOpen, mode, record, onSave, onClose }) => {
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [name, setName] = useState('');
  const [selectMode, setSelectMode] = useState<'ONE' | 'MANY'>('ONE');
  const [isInherited, setIsInherited] = useState(true);
  const [isDanger, setIsDanger] = useState(false);
  const [hint, setHint] = useState('');
  const [displayOrder, setDisplayOrder] = useState('10');
  const [isActive, setIsActive] = useState(true);
  const [rows, setRows] = useState<TagRow[]>([]);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [rowVersion, setRowVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bound = !!record?.bindsTo;

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setCodeTouched(false);

    if (mode === 'edit' && record) {
      setCode(record.tagGroupCode);
      setName(record.tagGroupName);
      setSelectMode(record.selectMode);
      setIsInherited(record.isInherited);
      setIsDanger(record.isDanger);
      setHint(record.hint ?? '');
      setDisplayOrder(String(record.displayOrder));
      setIsActive(record.isActive);
      setGroupId(record.id);
      setRowVersion(record.rowVersion);
      setRows((record.tags ?? []).map((t, i) => ({
        id: t.id, tagName: t.tagName, tagCode: t.tagCode ?? '', displayOrder: t.displayOrder || (i + 1) * 10,
      })));
    } else {
      setCode('');
      setName('');
      setSelectMode('ONE');
      setIsInherited(true);
      setIsDanger(false);
      setHint('');
      setDisplayOrder('10');
      setIsActive(true);
      setGroupId(null);
      setRowVersion(0);
      setRows([]);
    }
  }, [isOpen, mode, record]);

  const save = useCallback(async () => {
    if (!name.trim()) { setError('A tag group needs a name.'); return; }
    if (mode === 'create' && !code.trim()) { setError('A tag group needs a code.'); return; }
    if (mode === 'create' && !/^[A-Z][A-Z0-9_]{1,29}$/.test(code.trim())) {
      setError('The code is upper-case letters, digits and underscores, e.g. SPICE or SERVED_WITH.');
      return;
    }

    const named = rows.filter((r) => r.tagName.trim());
    const lower = named.map((r) => r.tagName.trim().toLowerCase());
    if (lower.length !== new Set(lower).size) { setError('Two tags have the same name.'); return; }
    if (bound && named.some((r) => !r.tagCode.trim())) {
      setError('Every tag of a bound group needs a code — it is the value written to the dish.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      /*
       * Header first, then the tags: two endpoints. If the second call fails
       * the group exists with no tags, which the grid shows and the user can
       * fix; the other order would leave tags with no group.
       */
      let id = groupId;
      const order = Number(displayOrder) || 0;
      if (id) {
        const saved = await dineOpsService.updateTagGroup(id, {
          tagGroupName: name.trim(), selectMode, isInherited, isDanger,
          hint: hint.trim() || undefined, displayOrder: order, isActive, rowVersion,
        });
        setRowVersion(saved.rowVersion);
      } else {
        const created = await dineOpsService.createTagGroup({
          tagGroupCode: code.trim(), tagGroupName: name.trim(), selectMode, isInherited, isDanger,
          hint: hint.trim() || undefined, displayOrder: order,
        });
        id = created.id;
        setGroupId(created.id);
        setRowVersion(created.rowVersion);
      }

      await dineOpsService.saveTags(
        id!,
        named.map((r, i) => ({
          id: r.id,
          tagName: r.tagName.trim(),
          tagCode: bound ? r.tagCode.trim() : undefined,
          displayOrder: r.displayOrder || (i + 1) * 10,
        })),
      );

      notificationService.success('Saved', `'${name.trim()}' saved with ${named.length} tag(s).`);
      onSave();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The tag group could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [mode, code, name, selectMode, isInherited, isDanger, hint, displayOrder, isActive, rows, bound, groupId, rowVersion, onSave, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'Add a tag group' : `Edit — ${name || 'tag group'}`}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            <IconSystem.x size={18} />
          </button>
        </div>

        <div className="modal-content" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {error && <div className="form-error-message" style={{ marginBottom: 12 }}>{error}</div>}

          {bound && (
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 10, padding: '6px 10px', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 6 }}>
              <IconSystem.lock size={12} /> This group is <strong>bound</strong>: its answer writes the dish&rsquo;s
              <code> {record?.bindsTo}</code> column. It cannot be deleted or made inheritable, and each tag needs a code.
            </div>
          )}

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (mode === 'create' && !codeTouched) setCode(codeFrom(e.target.value));
                }}
                placeholder="e.g., Spice, Allergens, Served with"
                maxLength={100}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="form-label">Code {mode === 'create' && <span className="text-danger">*</span>}</label>
              <input
                type="text"
                className="form-input"
                value={code}
                disabled={mode === 'edit'}
                onChange={(e) => { setCodeTouched(true); setCode(e.target.value.toUpperCase()); }}
                placeholder="SPICE"
                maxLength={30}
              />
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                {mode === 'create' ? 'Made from the name; change it if you like. Fixed once saved.' : 'A code never changes: reports and tests know the group by it.'}
              </div>
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Answers</label>
              <select className="form-input" value={selectMode} onChange={(e) => setSelectMode(e.target.value as 'ONE' | 'MANY')}>
                <option value="ONE">One — a dish gives a single answer (e.g. spice level)</option>
                <option value="MANY">Many — several can be true at once (e.g. allergens)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Order</label>
              <input type="number" className="form-input" value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)} min={0} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Hint</label>
            <input
              type="text"
              className="form-input"
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder="One line shown under the question on the Attributes tab"
              maxLength={200}
            />
          </div>

          <div className="form-group" style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }} title={bound ? 'A bound group is answered per dish' : ''}>
              <input type="checkbox" checked={isInherited} disabled={bound} onChange={(e) => setIsInherited(e.target.checked)} />
              <span>A menu group can answer for its dishes (inherited)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={isDanger} onChange={(e) => setIsDanger(e.target.checked)} />
              <span>Safety information — show in red, never hide (e.g. allergens)</span>
            </label>
            {mode === 'edit' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                <span>Active</span>
              </label>
            )}
          </div>

          <div style={{ fontSize: 12, opacity: 0.75, margin: '14px 0 8px' }}>
            The answers a dish can give. Saved as a whole: a tag removed here is removed from the group.
          </div>

          {rows.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'end', marginBottom: 8 }}>
              <div className="form-group" style={{ marginBottom: 0, flex: 2 }}>
                <label className="form-label">Tag</label>
                <input
                  type="text"
                  className="form-input"
                  value={r.tagName}
                  onChange={(e) => setRows((p) => p.map((x, j) => (j === i ? { ...x, tagName: e.target.value } : x)))}
                  placeholder="e.g., Mild"
                  maxLength={100}
                />
              </div>
              {bound && (
                <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                  <label className="form-label">Code</label>
                  <input
                    type="text"
                    className="form-input"
                    value={r.tagCode}
                    onChange={(e) => setRows((p) => p.map((x, j) => (j === i ? { ...x, tagCode: e.target.value } : x)))}
                    placeholder="value written"
                    maxLength={30}
                  />
                </div>
              )}
              <div className="form-group" style={{ marginBottom: 0, width: 90 }}>
                <label className="form-label">Order</label>
                <input
                  type="number"
                  className="form-input"
                  value={r.displayOrder}
                  onChange={(e) => setRows((p) => p.map((x, j) => (j === i ? { ...x, displayOrder: Number(e.target.value) || 0 } : x)))}
                  min={0}
                />
              </div>
              <button
                className="btn btn-secondary"
                style={{ marginBottom: 4 }}
                onClick={() => setRows((p) => p.filter((_, j) => j !== i))}
                title="Remove this tag"
              >
                <IconSystem.trash size={14} />
              </button>
            </div>
          ))}

          <button
            className="btn btn-secondary"
            onClick={() => setRows((p) => [...p, { tagName: '', tagCode: '', displayOrder: (p.length + 1) * 10 }])}
          >
            <IconSystem.add size={14} /> Add a tag
          </button>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
};

export default DineOpsTagGroupModal;
